import { useCallback } from 'react';

import { useNavigate, useSearch } from '@tanstack/react-router';

export interface UseSearchParamStateReturn<T> {
    value: T;
    setValue: (next: T | undefined) => void;
}

/**
 * A single URL search key as typed state: `value` reads `parse(rawSearchValue)`
 * and `setValue` writes it back, preserving sibling keys. This is the shared
 * primitive tabs and pagination sit on — see `SubfolderView.tsx`'s
 * `handleXFilterChange` callbacks for the shape this generalises.
 */
export function useSearchParamState<T>(
    key: string,
    parse: (raw: unknown) => T,
): UseSearchParamStateReturn<T> {
    const search = useSearch({ strict: false }) as Record<string, unknown>;
    const navigate = useNavigate();

    const value = parse(search[key]);

    const setValue = useCallback(
        (next: T | undefined) => {
            void navigate({
                search: (prev: Record<string, unknown>) => ({
                    ...prev,
                    [key]: next,
                }),
            } as Parameters<typeof navigate>[0]);
        },
        [navigate, key],
    );

    return { value, setValue };
}
